import {
  Box,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useStytchUser } from "@stytch/react";
import {
  AuthFactorInstance,
  AuthFactorInstanceProps,
} from "features/auth/AuthFactor";
import { AuthHelpButton } from "features/auth/AuthHelpButton";
import { useAuthCancel, useAuthContinue } from "features/auth/AuthUtils";
import { AuthFactorInstanceButton } from "features/auth/factor/AuthFactorButtons";
import { AuthFactorRemember } from "features/auth/factor/AuthFactorRemember";
import { AuthFactorOtp } from "features/auth/factor/otp/AuthFactorOtp";
import { AuthFactorRecovery } from "features/auth/factor/recovery/AuthFactorRecovery";
import { AuthFactorTotp } from "features/auth/factor/totp/AuthFactorTotp";
import { AuthFactorWebauthn } from "features/auth/factor/webauthn/AuthFactorWebauthn";
import { useStytchUserAuthFactorInstances } from "features/auth/StytchUtils";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { CardCloseButton } from "shared/components/card/CardCloseButton";

const AuthFactorElements = {
  totp: AuthFactorTotp,
  webauthn: AuthFactorWebauthn,
  otp: AuthFactorOtp,
  recovery: AuthFactorRecovery,
};

export const AuthFactorPage: React.FC = () => {
  const { t } = useTranslation(["auth", "common"]);

  const authContinue = useAuthContinue();
  const authCancel = useAuthCancel();

  const { user: stytchUser } = useStytchUser();
  const authFactors = useStytchUserAuthFactorInstances();

  const [authFactor, setAuthFactor] = useState<AuthFactorInstance>();
  const [rememberDevice, setRememberDevice] = useState(false);

  if (authFactor) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const AuthInstanceElement: React.FC<AuthFactorInstanceProps<any>> =
      AuthFactorElements[authFactor.type];
    return (
      <AuthInstanceElement
        instance={authFactor}
        onSuccess={authContinue}
        onCancel={() => setAuthFactor(undefined)}
        onSelectAuthFactor={setAuthFactor}
        rememberDevice={rememberDevice}
      />
    );
  }

  if (!stytchUser) {
    return <Navigate to="/auth/login" replace={true} />;
  }

  return (
    <Card width="md">
      <CardHeader>
        <Box>
          <Text textStyle="h4">{t("auth:authenticate")}</Text>
          <Text pt="24px">{t("auth:selectAuthMethod")}</Text>
        </Box>
        <CardCloseButton onClick={authCancel} />
      </CardHeader>
      <CardBody>
        <VStack alignItems="stretch" mb="16px">
          {authFactors.map((instance) => (
            <AuthFactorInstanceButton
              key={instance.id}
              instance={instance}
              onClick={setAuthFactor}
            />
          ))}
        </VStack>
        <AuthFactorRemember
          rememberDevice={rememberDevice}
          setRememberDevice={setRememberDevice}
        />
      </CardBody>
      <CardFooter>
        <AuthHelpButton />
      </CardFooter>
    </Card>
  );
};
